/* eslint-disable max-statements */
import { openDB, deleteDB, wrap, unwrap, IDBPDatabase } from 'idb';
import {
    Operation,
    SalesDocument,
    SalesDocumentPosition,
    SalesDocumentConnection,
    DocumentAttachment,
    SalesDocumentConnections,
    dbVersion,
    SerializedClock,
    operationIndexFunc,
    ClockService,
    DocumentType,
    Delivery,
    ComplaintAppointment,
    ComplaintSparePart,
    ComplaintNote,
    OpenedDocument,
} from '@icc/b2b/shared';
import { logger } from '@icc/shared';
import { getDateFromDuration } from '@icc/shared/helpers';

export class IDBService {
    _db!: Promise<IDBPDatabase<unknown>>;
    dbName!: string;

    protected resolve;

    constructor() {
        this._db = new Promise((resolve) => {
            this.resolve = resolve;
        });
    }

    async getDB() {
        try {
            const db = await this._db;
            const tx = db.transaction('docs');
            await tx.done;
        } catch (err) {
            logger.error('IDB error while opening db', err);
            await this.close();
            await this.initializeDatabase();
        }
        return this._db;
    }

    async open(user, envDbVersion: number, dbUpdatedCallback: () => void = () => {}) {
        this.dbName = this.getDBName(user, dbVersion, envDbVersion);
        await this.initializeDatabase(dbUpdatedCallback);
    }

    private async initializeDatabase(dbUpdatedCallback: () => void = () => {}) {
        const idb = await openDB(this.dbName, 14, {
            upgrade(db, oldVersion, newVersion, transaction) {
                if (!db.objectStoreNames.contains('docs')) {
                    db.createObjectStore('docs', {
                        keyPath: 'uuid',
                    });
                }
                if (!db.objectStoreNames.contains('ops')) {
                    db.createObjectStore('ops', {
                        keyPath: 'id',
                    });
                }
                if (!db.objectStoreNames.contains('positions')) {
                    db.createObjectStore('positions', {
                        keyPath: 'uuid',
                    });
                }
                if (!db.objectStoreNames.contains('appointments')) {
                    db.createObjectStore('appointments', {
                        keyPath: 'uuid',
                    });
                }
                if (!db.objectStoreNames.contains('complaint_notes')) {
                    db.createObjectStore('complaint_notes', {
                        keyPath: 'uuid',
                    });
                }
                if (!db.objectStoreNames.contains('complaint_spare_parts')) {
                    db.createObjectStore('complaint_spare_parts', {
                        keyPath: 'uuid',
                    });
                }
                if (!db.objectStoreNames.contains('attachments')) {
                    db.createObjectStore('attachments', {
                        keyPath: 'uuid',
                    });
                }
                if (!db.objectStoreNames.contains('document_connections')) {
                    db.createObjectStore('document_connections', {
                        keyPath: 'uuid',
                    });
                }
                if (!db.objectStoreNames.contains('operation_graphs')) {
                    db.createObjectStore('operation_graphs', {
                        keyPath: 'id',
                    });
                }
                if (!db.objectStoreNames.contains('opened_documents')) {
                    db.createObjectStore('opened_documents', {
                        keyPath: 'uuid',
                    });
                }
                if (!db.objectStoreNames.contains('deliveries')) {
                    db.createObjectStore('deliveries', {
                        keyPath: 'uuid',
                    });
                }
                if (!transaction.objectStore('docs').indexNames.contains('created')) {
                    transaction.objectStore('docs').createIndex('created', 'created', {
                        unique: false,
                    });
                }
                if (!transaction.objectStore('positions').indexNames.contains('salesDocumentId')) {
                    transaction
                        .objectStore('positions')
                        .createIndex('salesDocumentId', 'salesDocumentId', {
                            unique: false,
                        });
                }
                if (!transaction.objectStore('deliveries').indexNames.contains('documentId')) {
                    transaction
                        .objectStore('deliveries')
                        .createIndex('documentId', 'documentId', {
                            unique: false,
                        });
                }
                if (!transaction.objectStore('appointments').indexNames.contains('documentId')) {
                    transaction
                        .objectStore('appointments')
                        .createIndex('documentId', 'documentId', {
                            unique: false,
                        });
                }
                if (!transaction.objectStore('complaint_notes').indexNames.contains('documentId')) {
                    transaction
                        .objectStore('complaint_notes')
                        .createIndex('documentId', 'documentId', {
                            unique: false,
                        });
                }
                if (!transaction.objectStore('complaint_spare_parts').indexNames.contains('documentId')) {
                    transaction
                        .objectStore('complaint_spare_parts')
                        .createIndex('documentId', 'documentId', {
                            unique: false,
                        });
                }
                if (!transaction.objectStore('attachments').indexNames.contains('salesDocumentId')) {
                    transaction
                        .objectStore('attachments')
                        .createIndex('salesDocumentId', 'salesDocumentId', {
                            unique: false,
                        });
                }
                if (!transaction
                    .objectStore('document_connections')
                    .indexNames.contains('salesDocumentId')) {
                    transaction
                        .objectStore('document_connections')
                        .createIndex('salesDocumentId', 'salesDocumentId', {
                            unique: false,
                        });
                }
                if (!transaction
                    .objectStore('document_connections')
                    .indexNames.contains('buyDocumentId')) {
                    transaction
                        .objectStore('document_connections')
                        .createIndex('buyDocumentId', 'buyDocumentId', {
                            unique: false,
                        });
                }
                if (!transaction.objectStore('ops').indexNames.contains('timestamp')) {
                    transaction.objectStore('ops').createIndex('timestamp', 'timestamp', {
                        unique: true,
                    });
                }
                if (!transaction.objectStore('ops').indexNames.contains('docId')) {
                    transaction.objectStore('ops').createIndex('docId', 'docId', {
                        unique: false,
                    });
                }
                if (!transaction.objectStore('ops').indexNames.contains('buyDocId')) {
                    transaction.objectStore('ops').createIndex('buyDocId', 'buyDocId', {
                        unique: false,
                    });
                }

                if (!transaction.objectStore('docs').indexNames.contains('rootId')) {
                    transaction.objectStore('docs').createIndex('rootId', 'rootId', {
                        unique: false,
                    });
                }

                if (oldVersion < 8 && !transaction.objectStore('positions').indexNames.contains('documentId')) {
                    transaction
                        .objectStore('positions')
                        .createIndex('documentId', 'documentId', {
                            unique: false,
                        });
                }
                if (oldVersion >= 8 && transaction.objectStore('positions').indexNames.contains('salesDocumentId')) {
                    transaction
                        .objectStore('positions')
                        .deleteIndex('salesDocumentId');
                }
                if (oldVersion < 8 && !transaction.objectStore('appointments').indexNames.contains('documentId')) {
                    transaction
                        .objectStore('appointments')
                        .createIndex('documentId', 'documentId', {
                            unique: false,
                        });
                }
                if (oldVersion >= 8 && transaction.objectStore('appointments').indexNames.contains('salesDocumentId')) {
                    transaction
                        .objectStore('appointments')
                        .deleteIndex('salesDocumentId');
                }
                if (oldVersion < 8 && !transaction.objectStore('complaint_notes').indexNames.contains('documentId')) {
                    transaction
                        .objectStore('complaint_notes')
                        .createIndex('documentId', 'documentId', {
                            unique: false,
                        });
                }
                if (oldVersion >= 8 && transaction.objectStore('complaint_notes').indexNames.contains('salesDocumentId')) {
                    transaction
                        .objectStore('complaint_notes')
                        .deleteIndex('salesDocumentId');
                }
                if (oldVersion < 8 && !transaction.objectStore('complaint_spare_parts').indexNames.contains('documentId')) {
                    transaction
                        .objectStore('complaint_spare_parts')
                        .createIndex('documentId', 'documentId', {
                            unique: false,
                        });
                }
                if (oldVersion >= 8 && transaction.objectStore('complaint_spare_parts').indexNames.contains('salesDocumentId')) {
                    transaction
                        .objectStore('complaint_spare_parts')
                        .deleteIndex('salesDocumentId');
                }
                if (oldVersion < 8 && !transaction.objectStore('attachments').indexNames.contains('documentId')) {
                    transaction
                        .objectStore('attachments')
                        .createIndex('documentId', 'documentId', {
                            unique: false,
                        });
                }
                if (oldVersion >= 8 && transaction.objectStore('attachments').indexNames.contains('salesDocumentId')) {
                    transaction
                        .objectStore('attachments')
                        .deleteIndex('salesDocumentId');
                }

                if (!transaction.objectStore('opened_documents').indexNames.contains('date')) {
                    transaction
                        .objectStore('opened_documents')
                        .createIndex('date', 'date', {
                            unique: false,
                        });
                }

                dbUpdatedCallback();
            },
            blocked() {
                logger.error('IDB blocked');
            },
            blocking() {
                logger.error('IDB is blocking');
            },
            terminated() {
                logger.error('IDB connection is terminated');
            },
        });
        this.resolve(idb);
        logger.log('DB', idb.name);
    }

    async close() {
        const db = await this._db;
        db.close();
        this._db = new Promise((resolve) => {
            this.resolve = resolve;
        });
    }

    async clear(userId: number, envDbVersion: number) {
        const db = await this.getDB();
        db.clear('docs');
        db.clear('ops');
        db.clear('positions');
        db.clear('appointments');
        db.clear('complaint_notes');
        db.clear('complaint_spare_parts');
        db.clear('attachments');
        db.clear('deliveries');
        db.clear('document_connections');
        db.clear('operation_graphs');
        db.clear('opened_documents');
    }

    // async getAllDb(kind: 'docs'): Promise<SalesDocument[]>;
    // async getAllDb(kind: 'positions'): Promise<SalesDocumentPosition[]>;
    // async getAllDb(kind: 'ops'): Promise<Operation[]>;
    // async getAllDb(kind: 'document_connections'): Promise<SalesDocumentConnection[]>;
    // async getAllDb(kind: 'attachments'): Promise<ComplaintAttachment[]>;
    // async getAllDb(kind: 'operation_graphs'): Promise<SalesDocumentConnections[]>;
    async getAllDb(
        kind:
            | 'docs'
            | 'positions'
            | 'appointments'
            | 'complaint_notes'
            | 'complaint_spare_parts'
            | 'ops'
            | 'document_connections'
            | 'attachments'
            | 'deliveries'
            | 'operation_graphs'
            | 'opened_documents',
    ): Promise<any[]> {
        const db = await this.getDB();
        const tx = db.transaction(kind, 'readonly', {
            durability: 'relaxed',
        });
        let cursor = await tx.store.openCursor();
        const docs = [];
        while (cursor) {
            docs.push(cursor.value);
            cursor = await cursor.continue();
        }
        await tx.done;
        return docs;
    }

    async getAllKeysDb(
        kind:
        | 'docs'
        | 'positions'
        | 'appointments'
        | 'complaint_notes'
        | 'complaint_spare_parts'
        | 'ops'
        | 'document_connections'
        | 'attachments'
        | 'deliveries'
        | 'operation_graphs'
        | 'opened_documents',
        index?: string
    ): Promise<string[]> {
        const db = await this.getDB();
        const tx = db.transaction(kind, 'readonly', {
            durability: 'relaxed',
        });
        const keys = await tx.store.getAllKeys(index);
        return keys.map(key => key.toString());
    }


    async getManyDb(kind: 'docs', ids: string[]): Promise<SalesDocument[]>;
    async getManyDb(kind: 'positions', ids: string[]): Promise<SalesDocumentPosition[]>;
    async getManyDb(kind: 'appointments', ids: string[]): Promise<ComplaintAppointment[]>;
    async getManyDb(kind: 'complaint_notes', ids: string[]): Promise<ComplaintNote[]>;
    async getManyDb(kind: 'complaint_spare_parts', ids: string[]): Promise<ComplaintSparePart[]>;
    async getManyDb(kind: 'ops', ids: string[]): Promise<Operation[]>;
    async getManyDb(
        kind: 'document_connections',
        ids: string[]
    ): Promise<SalesDocumentConnection[]>;
    async getManyDb(kind: 'attachments', ids: string[]): Promise<DocumentAttachment[]>;
    async getManyDb(kind: 'operation_graphs', ids: string[]): Promise<SalesDocumentConnections[]>;
    async getManyDb(kind: 'deliveries', ids: string[]): Promise<Delivery[]>;
    async getManyDb(kind: 'opened_documents', ids: string[]): Promise<OpenedDocument[]>;
    async getManyDb(
        kind:
            | 'docs'
            | 'positions'
            | 'appointments'
            | 'complaint_notes'
            | 'complaint_spare_parts'
            | 'ops'
            | 'document_connections'
            | 'attachments'
            | 'operation_graphs'
            | 'deliveries'
            | 'opened_documents',
        ids: string[]
    ): Promise<any[]> {
        if (!ids.length) return [];
        const db = await this.getDB();
        const tx = db.transaction(kind, 'readonly', {
            durability: 'relaxed',
        });
        const docs = [];
        for (const id of ids) {
            const el = await tx.store.get(id);
            if (el) {
                docs.push(el);
            }
        }
        await tx.done;
        return docs;
    }

    async getManyFromIndexDb(
        kind:
            | 'docs'
            | 'positions'
            | 'appointments'
            | 'complaint_notes'
            | 'complaint_spare_parts'
            | 'ops'
            | 'document_connections'
            | 'attachments'
            | 'operation_graphs'
            | 'deliveries'
            | 'opened_documents',
        index: string,
        ids: string[]
    ): Promise<any[]> {
        if (!ids.length) return [];
        ids.sort();
        const db = await this.getDB();
        const tx = db.transaction(kind, 'readonly', {
            durability: 'relaxed',
        });
        const docs = [];
        for (const id of ids) {
            const docsForId = await tx.store.index(index).getAll(id);
            if (docsForId) {
                for (const el of docsForId) {
                    docs.push(el);
                }
            }
        }
        await tx.done;
        return docs;
    }

    async getDocumentsByRootId(rootIds: string[]) {
        return this.getManyFromIndexDb('docs', 'rootId', rootIds);
    }

    async getMany(ids: string[], pairs: string[][] | null = null, minTimestamp = '') {
        const salesDocumentIds = pairs?.map((p) => p[0]) ?? ids;
        const buyDocumentIds = pairs?.map((p) => p[1]) ?? ids;
        if (!pairs) {
            const documentConnectionsInit = await this.getManyFromIndexDb(
                'document_connections',
                'salesDocumentId',
                ids
            );
            const buyDocumentConnectionsInit = await this.getManyFromIndexDb(
                'document_connections',
                'buyDocumentId',
                ids
            );
            for (const con of documentConnectionsInit) {
                if (!buyDocumentIds.includes(con.buyDocumentId)) {
                    buyDocumentIds.push(con.buyDocumentId);
                }
            }
            for (const con of buyDocumentConnectionsInit) {
                if (!salesDocumentIds.includes(con.salesDocumentId)) {
                    salesDocumentIds.push(con.salesDocumentId);
                }
            }
        }

        const docs = await this.getManyDb('docs', ids);
        const positionsArray = await this.getManyFromIndexDb('positions', 'documentId', ids);
        const appointmentsArray = await this.getManyFromIndexDb('appointments', 'documentId', ids);
        const complaintNotesArray = await this.getManyFromIndexDb('complaint_notes', 'documentId', ids);
        const complaintSparePartsArray = await this.getManyFromIndexDb('complaint_spare_parts', 'documentId', ids);
        const attachments = await this.getManyFromIndexDb('attachments', 'documentId', ids);
        const documentConnectionsDb = await this.getManyFromIndexDb(
            'document_connections',
            'salesDocumentId',
            salesDocumentIds
        );
        const buyDocumentConnectionsDb = await this.getManyFromIndexDb(
            'document_connections',
            'buyDocumentId',
            buyDocumentIds
        );
        const deliveriesArray = await this.getManyFromIndexDb('deliveries', 'documentId', ids);
        const ops = await this.getManyOps(ids, minTimestamp);

        const documents: Record<string, SalesDocument> = {};
        const documentsIds: string[] = [];
        const documentTypes = new Map<string, DocumentType>();
        for (const doc of docs) {
            documents[doc.uuid] = doc;
            documentsIds.push(doc.uuid);
            documentTypes.set(doc.uuid, doc.type);
        }
        const positions: Record<string, SalesDocumentPosition> = {};
        for (const pos of positionsArray) {
            positions[pos.uuid] = pos;
        }
        const appointments: Record<string, ComplaintAppointment> = {};
        for (const app of appointmentsArray) {
            appointments[app.uuid] = app;
        }
        const complaintNotes: Record<string, ComplaintNote> = {};
        for (const note of complaintNotesArray) {
            complaintNotes[note.uuid] = note;
        }
        const complaintSpareParts: Record<string, ComplaintSparePart> = {};
        for (const csp of complaintSparePartsArray) {
            complaintSpareParts[csp.uuid] = csp;
        }

        const documentConnections: SalesDocumentConnection[] = [];
        const documentConnectionsIndex: Record<string, boolean> = {};
        documentConnectionsDb.forEach((con) => {
            if (
                pairs?.some((p) => p[0] === con.salesDocumentId && p[1] === con.buyDocumentId) ||
                (salesDocumentIds.includes(con.salesDocumentId) &&
                    buyDocumentIds.includes(con.buyDocumentId))
            ) {
                documentConnections.push(con);
                documentConnectionsIndex[con.uuid] = true;
            }
        });
        buyDocumentConnectionsDb.forEach((con) => {
            if (
                !documentConnectionsIndex[con.uuid] &&
                (pairs?.some((p) => p[0] === con.salesDocumentId && p[1] === con.buyDocumentId) ||
                    (salesDocumentIds.includes(con.salesDocumentId) &&
                        buyDocumentIds.includes(con.buyDocumentId)))
            ) {
                documentConnections.push(con);
                documentConnectionsIndex[con.uuid] = true;
            }
        });

        const deliveries: Record<string, Delivery> = {};
        for (const delivery of deliveriesArray) {
            deliveries[delivery.uuid] = delivery;
        }

        return {
            documents,
            documentsIds,
            documentTypes,
            ops,
            positions,
            appointments,
            complaintNotes,
            complaintSpareParts,
            attachments,
            documentConnections,
            deliveries,
        };
    }

    async getManyOps(ids: string[], minTimestamp = '') {
        if (!ids.length) return [];
        await this.getDB();
        let ops: Operation[] = [];
        const opsUnorderedSales = await this.getManyFromIndexDb('ops', 'docId', ids);
        const opsUnorderedBuy = await this.getManyFromIndexDb('ops', 'buyDocId', ids);
        const opsUnordered: Operation[] = [];
        const opsIndex: Record<string, boolean> = {};
        opsUnorderedSales.forEach((con) => {
            if (!opsIndex[con.id]) {
                opsUnordered.push(con);
                opsIndex[con.id] = true;
            }
        });
        opsUnorderedBuy.forEach((con) => {
            if (!opsIndex[con.id]) {
                opsUnordered.push(con);
                opsIndex[con.id] = true;
            }
        });
        ops = opsUnordered.sort((a, b) => {
            if (a.timestamp < b.timestamp) {
                return 1;
            }
            if (a.timestamp > b.timestamp) {
                return -1;
            }
            return 0;
        });
        return ops;
    }

    async getInitialState(openedDocumentId: string) {
        const neededDocsIds = openedDocumentId ? [openedDocumentId] : [];
        const documentConnectionsDb = await this.getManyFromIndexDb(
            'document_connections',
            'salesDocumentId',
            neededDocsIds
        );
        for (const con of documentConnectionsDb) {
            if (!neededDocsIds.includes(con.buyDocumentId)) {
                neededDocsIds.push(con.buyDocumentId);
            }
        }
        const docs = await this.getManyDb('docs', neededDocsIds);
        const positionsArray = await this.getManyFromIndexDb(
            'positions',
            'documentId',
            neededDocsIds
        );
        const appointmentsArray = await this.getManyFromIndexDb(
            'appointments',
            'documentId',
            neededDocsIds
        );
        const complaintNotesArray = await this.getManyFromIndexDb(
            'complaint_notes',
            'documentId',
            neededDocsIds
        );
        const complaintSparePartsArray = await this.getManyFromIndexDb(
            'complaint_spare_parts',
            'documentId',
            neededDocsIds
        );
        const attachments = await this.getManyFromIndexDb(
            'attachments',
            'documentId',
            neededDocsIds
        );
        const ops = await this.getManyOps(neededDocsIds);
        const buyDocumentConnectionsDb = await this.getManyFromIndexDb(
            'document_connections',
            'buyDocumentId',
            neededDocsIds
        );
        const deliveriesArray = await this.getManyFromIndexDb('deliveries', 'documentId', neededDocsIds);

        const documents: Record<string, SalesDocument> = {};
        const documentsIds: string[] = [];
        const documentTypes = new Map<string, DocumentType>();
        for (const doc of docs) {
            documents[doc.uuid] = doc;
            documentsIds.push(doc.uuid);
            documentTypes.set(doc.uuid, doc.type);
        }
        const positions: Record<string, SalesDocumentPosition> = {};
        for (const pos of positionsArray) {
            positions[pos.uuid] = pos;
        }
        const appointments: Record<string, ComplaintAppointment> = {};
        for (const app of appointmentsArray) {
            appointments[app.uuid] = app;
        }
        const complaintNotes: Record<string, ComplaintNote> = {};
        for (const note of complaintNotesArray) {
            complaintNotes[note.uuid] = note;
        }
        const complaintSpareParts: Record<string, ComplaintSparePart> = {};
        for (const csp of complaintSparePartsArray) {
            complaintSpareParts[csp.uuid] = csp;
        }

        const documentConnections: SalesDocumentConnections = {};
        [documentConnectionsDb, buyDocumentConnectionsDb].forEach((connections) => {
            connections.forEach((con) => {
                if (!documentConnections[con.salesDocumentId]) {
                    documentConnections[con.salesDocumentId] = {};
                }
                if (
                    !documentConnections[con.salesDocumentId][
                        con.buyDocumentId + (con.isDeleted ? '^' : '')
                    ]
                ) {
                    documentConnections[con.salesDocumentId][
                        con.buyDocumentId + (con.isDeleted ? '^' : '')
                    ] = {};
                }
                documentConnections[con.salesDocumentId][
                    con.buyDocumentId + (con.isDeleted ? '^' : '')
                ][con.uuid] = con;
            });
        });

        const deliveries: Record<string, Delivery> = {};
        for (const delivery of deliveriesArray) {
            deliveries[delivery.uuid] = delivery;
        }

        const operationsKeysMap: Map<string, Operation> = new Map();
        const operationsIds: Map<string, true> = new Map();
        for (const msg of ops) {
            const key = msg.index || operationIndexFunc(msg);
            if (!operationsKeysMap.has(key)) {
                operationsKeysMap.set(key, msg);
            }
            operationsIds.set(msg.id, true);
        }
        return {
            documents,
            documentsIds,
            documentTypes,
            ops,
            positions,
            appointments,
            complaintNotes,
            complaintSpareParts,
            attachments,
            documentConnections,
            operationsKeysMap,
            operationsIds,
            deliveries,
        };
    }

    // async saveMany(kind: 'docs', docs: SalesDocument[]): Promise<void>;
    // async saveMany(kind: 'positions', docs: SalesDocumentPosition[]): Promise<void>;
    // async saveMany(kind: 'ops', docs: Operation[]): Promise<void>;
    // async saveMany(kind: 'document_connections', docs: SalesDocumentConnection[]): Promise<void>;
    // async saveMany(kind: 'attachments', docs: ComplaintAttachment[]): Promise<void>;
    async saveMany(
        kind: 'docs' | 'positions' | 'appointments' | 'complaint_notes' | 'complaint_spare_parts' | 'ops' | 'document_connections' | 'attachments' | 'deliveries' | 'opened_documents',
        docs: any[]
    ): Promise<void> {
        const db = await this.getDB();
        const tx = db.transaction(kind, 'readwrite', {
            durability: 'relaxed',
        });
        for (const doc of docs) {
            if (doc.$dirty) {
                delete doc.$dirty;
            }
            tx.store.put(doc);
        }
        await tx.done;
    }

    // async deleteMany(kind: 'docs', docs: SalesDocument['uuid'][]): Promise<void>;
    // async deleteMany(kind: 'positions', docs: SalesDocumentPosition['uuid'][]): Promise<void>;
    // async deleteMany(kind: 'ops', docs: Operation['id'][]): Promise<void>;
    // async deleteMany(
    //     kind: 'document_connections',
    //     docs: SalesDocumentConnection['uuid'][]
    // ): Promise<void>;
    // async deleteMany(kind: 'attachments', docs: ComplaintAttachment['uuid'][]): Promise<void>;
    async deleteMany(
        kind: 'docs' | 'positions' | 'appointments' | 'complaint_notes' | 'complaint_spare_parts' | 'ops' | 'document_connections' | 'attachments' | 'deliveries' | 'opened_documents',
        docs: any[]
    ): Promise<void> {
        const db = await this.getDB();
        const tx = db.transaction(kind, 'readwrite', {
            durability: 'relaxed',
        });
        docs.map((doc) => {
            if (doc.$dirty) {
                delete doc.$dirty;
            }
            tx.store.delete(doc);
        });
        await tx.done;
    }

    async removeDocumentPermanently(documentsId: string[], userId: number) {
        const serializedClock = await this.getSyncTrie();
        if (serializedClock) {
            for (const documentId of documentsId) {
                if (ClockService.getClock().merklesMap[documentId]) {
                    delete ClockService.getClock().merklesMap[documentId];
                    ClockService.deletedDocs.push(documentId);
                }
            }
            await this.saveSyncTrie(
                ClockService.serializeClock(ClockService.getClock()),
                {},
                ClockService.deletedDocs
            );
        }
        const db = await this.getDB();
        await this.deleteMany('docs', documentsId);
        const positionsArray: SalesDocumentPosition[] = await this.getManyFromIndexDb(
            'positions',
            'documentId',
            documentsId
        );
        await this.deleteMany(
            'positions',
            positionsArray
                .filter((el) => documentsId.includes(el.documentId))
                .map((el) => el.uuid)
        );
        const attachments: DocumentAttachment[] = await this.getManyFromIndexDb(
            'attachments',
            'documentId',
            documentsId
        );
        await this.deleteMany(
            'attachments',
            attachments
                .filter((el) => documentsId.includes(el.documentId))
                .map((el) => el.uuid)
        );
        const ops: Operation[] = await this.getManyOps(documentsId);
        await this.deleteMany(
            'ops',
            ops
                .filter(
                    (el) =>
                        (documentsId.includes(el.docId) ||
                            ('buyDocId' in el && documentsId.includes(el.buyDocId))) &&
                        el.del > ('buyDocId' in el && el.buyDocId ? 1 : 0)
                )
                .map((el) => el.id)
        );
        const documentConnectionsDb: SalesDocumentConnection[] = await this.getManyFromIndexDb(
            'document_connections',
            'salesDocumentId',
            documentsId
        );
        const buyDocumentConnectionsDb: SalesDocumentConnection[] = await this.getManyFromIndexDb(
            'document_connections',
            'buyDocumentId',
            documentsId
        );
        await this.deleteMany(
            'document_connections',
            documentConnectionsDb
                .filter(
                    (el) =>
                        documentsId.includes(el.salesDocumentId) ||
                        documentsId.includes(el.buyDocumentId)
                )
                .map((el) => el.uuid)
        );
        await this.deleteMany(
            'document_connections',
            buyDocumentConnectionsDb
                .filter(
                    (el) =>
                        documentsId.includes(el.salesDocumentId) ||
                        documentsId.includes(el.buyDocumentId)
                )
                .map((el) => el.uuid)
        );

        const deliveriesArray: Delivery[] = await this.getManyFromIndexDb('deliveries', 'documentId', documentsId);
        await this.deleteMany(
            'deliveries',
            deliveriesArray
                .filter((el) => documentsId.includes(el.documentId))
                .map((el) => el.uuid)
        );

        const appointmentsArray: ComplaintAppointment[] = await this.getManyFromIndexDb(
            'appointments',
            'documentId',
            documentsId
        );
        await this.deleteMany(
            'appointments',
            appointmentsArray
                .filter((el) => documentsId.includes(el.documentId))
                .map((el) => el.uuid)
        );

        const complaintNotesArray: ComplaintNote[] = await this.getManyFromIndexDb(
            'complaint_notes',
            'documentId',
            documentsId
        );
        await this.deleteMany(
            'complaint_notes',
            complaintNotesArray
                .filter((el) => documentsId.includes(el.documentId))
                .map((el) => el.uuid)
        );

        const complaintSparePartsArray: ComplaintSparePart[] = await this.getManyFromIndexDb(
            'complaint_spare_parts',
            'documentId',
            documentsId
        );
        await this.deleteMany(
            'complaint_spare_parts',
            complaintSparePartsArray
                .filter((el) => documentsId.includes(el.documentId))
                .map((el) => el.uuid)
        );

        await this.deleteMany(
            'opened_documents',
            documentsId
        );

        logger.log('Removed documents', documentsId);
    }

    async getSyncTrie(): Promise<SerializedClock> {
        try {
            const db = await this.getDB();
            const data = await db.getAll('operation_graphs');
            const docsIds = await this.getAllKeysDb('docs');
            const docsIdsMap: Record<string, boolean> = {};
            for (const id of docsIds) {
                docsIdsMap[id] = true;
            }
            const trie: SerializedClock = {
                timestamp: '',
                merklesMap: {},
                lockedMap: {},
            };
            for (const el of data) {
                if (el.id === 'timestamp') {
                    trie.timestamp = el.data;
                } else if (!el.data?.hasAdd || docsIdsMap[el.id]) {
                    trie.merklesMap[el.id] = el.data;
                    trie.lockedMap[el.id] = el.locked;
                }
            }
            if (trie.timestamp === '') {
                return null;
            }
            return trie;
        } catch (err) {
            return null;
        }
    }

    async saveSyncTrie(
        trie: SerializedClock,
        updated?: Record<string, number>,
        deleted?: string[]
    ) {
        const db = await this.getDB();
        const tx = db.transaction('operation_graphs', 'readwrite', {
            durability: 'relaxed',
        });
        tx.store.put({
            id: 'timestamp',
            data: trie.timestamp,
        });
        for (const key in trie.merklesMap) {
            if ((!updated || updated[key]) && (!deleted || !deleted.includes(key))) {
                if (Object.prototype.hasOwnProperty.call(trie.merklesMap, key)) {
                    tx.store.put({
                        id: key,
                        data: trie.merklesMap[key],
                        locked: trie.lockedMap[key],
                    });
                }
            }
        }
        if (deleted?.length) {
            for (const key of deleted) {
                tx.store.delete(key);
            }
        }
        await tx.done;
    }

    async setOpenedDocuments(openedDocumentsId: string[]) {
        if (!openedDocumentsId.length) return;
        for (const id of openedDocumentsId) {
            ClockService.localLastOpened[id] = id;
        }
        const db = await this.getDB();
        const tx = db.transaction('opened_documents', 'readwrite', {
            durability: 'relaxed',
        });
        for (const id of openedDocumentsId) {
            tx.store.put({
                uuid: id,
                date: new Date(),
            });
        }
        await tx.done;
    }

    async getLastOpenedDocuments(period: string) {
        const date = getDateFromDuration(period);
        const db = await this.getDB();
        const tx = db.transaction('opened_documents', 'readonly', {
            durability: 'relaxed',
        });
        let cursor = await tx.store.index('date').openCursor(IDBKeyRange.lowerBound(date, true));
        const docIdsMap = new Map<string, boolean>();

        while (cursor) {
            docIdsMap.set(cursor.value.uuid, true);
            cursor = await cursor.continue();
        }
        return docIdsMap;
    }

    protected getDBName(user: any, version: number, envDbVersion?: number) {
        if (envDbVersion) {
            return `iccomplex-trade-${version}-e${envDbVersion}-${user.id}-${user.ActiveMarket}-${user.ActiveCompany}`;
        }
        return `iccomplex-trade-${version}-${user.id}-${user.ActiveMarket}-${user.ActiveCompany}`;
    }
}
